// Polish Phase 1/3 (AUDIO-04 / AUDIO-06) — the procedural cue bank.
//
// Every gameplay + cinematic SFX the canon calls for, synthesized at play time
// from synth.ts primitives and tinted by the active AudioThemeProfile. Far
// exceeds BrandIdentity §13's "~15 effect" bank: 25 cues covering the three
// signatures (trace whisper, token clink, queen organ), the UI tactile bank,
// the word/match feedback bank, empower/capture, and the layered big moments
// (flop/river drumroll+stinger, queen, power-up,
// win/lose, board flip).
//
// Each cue is a pure function (ctx, dest, profile, opts) => void that schedules
// nodes and returns. Layering for big moments happens inside the cue (multiple
// voices). Ducking WOULD be the engine's job (engine.duck()), but no cue
// dispatch wires it yet — it activates with the future ambient bed (AUD-4).

import {
  playChord,
  playNoise,
  playTone,
  type SynthContext,
  semitone,
} from './synth'
import type { AudioThemeProfile } from './theme-audio'

export type SfxCue =
  // --- UI tactile bank ---
  | 'ui-tap'
  | 'ui-confirm'
  | 'ui-back' // reserved — no production caller yet (INT-02)
  | 'ui-toggle'
  | 'ui-error' // non-adjacent / cell-reuse trace rejection (WordTraceLayer)
  // --- word / match feedback ---
  | 'trace-step' // signature: rising whisper-chime per letter
  | 'word-valid'
  | 'word-invalid'
  | 'hollow-reject'
  | 'token-clink' // signature: bright golden coin
  | 'cube-spawn' // rune-carve shimmer — reserved (would stack on cascade)
  | 'anchor-place' // stone-carve warm bell
  | 'anchor-consume' // stone-fall deep impact
  | 'affinity-ping'
  // --- empower / pieces ---
  | 'empower' // organ chord pitched per role (opts.pitch 0..3)
  | 'capture'
  | 'king-capture'
  // --- cinematics / big moments ---
  | 'flop'
  | 'river'
  | 'grow-impact' // the court opens at the center seam — deep grow-thud
  | 'queen' // signature: rich organ chord ~700ms
  | 'powerup'
  | 'win-fanfare'
  | 'lose-sting'
  | 'board-flip'
  | 'reveal-pulse'

export interface CueOptions {
  /** Sequence index (trace-step pitch climb, cascade stagger). */
  readonly index?: number
  /** Role/voice pitch selector for empower (0 Knight … 3 Queen). */
  readonly pitch?: number
  /** Loudness scale [0,1] (default 1). */
  readonly intensity?: number
}

/** Reference pitch for the active theme (A4 transposed to the court's
 * register). All cues derive their notes from this. */
function base(profile: AudioThemeProfile): number {
  return semitone(440, profile.transpose)
}

/** Pentatonic-ish ascending ladder for the trace whisper — successive letters
 * climb so a long word sings. */
const TRACE_LADDER = [0, 2, 4, 7, 9, 12, 14, 16, 19, 21]

type CueFn = (
  ctx: SynthContext,
  dest: AudioNode,
  profile: AudioThemeProfile,
  opts: CueOptions,
) => void

export const CUES: Readonly<Record<SfxCue, CueFn>> = {
  // ---------------------------------------------------------------- UI bank
  'ui-tap': (ctx, dest, profile, o) => {
    const g = 0.12 * (o.intensity ?? 1)
    playTone(ctx, dest, {
      freq: semitone(base(profile), 12),
      type: profile.tonalType,
      duration: 0.008,
      attack: 0.002,
      release: 0.04,
      gain: g,
      lowpass: 3200 * profile.brightness,
    })
    playNoise(ctx, dest, {
      duration: 0.012,
      release: 0.03,
      gain: g * 0.5,
      filter: { type: 'highpass', freq: 2000 },
    })
  },
  'ui-confirm': (ctx, dest, profile, o) => {
    const b = base(profile)
    const g = 0.18 * (o.intensity ?? 1)
    playTone(ctx, dest, {
      freq: b,
      type: profile.tonalType,
      duration: 0.05,
      release: 0.08,
      gain: g,
    })
    playTone(ctx, dest, {
      freq: semitone(b, 7),
      type: profile.tonalType,
      delay: 0.06,
      duration: 0.06,
      release: 0.1,
      gain: g,
    })
  },
  'ui-back': (ctx, dest, profile, o) => {
    const b = base(profile)
    const g = 0.14 * (o.intensity ?? 1)
    playTone(ctx, dest, {
      freq: semitone(b, 5),
      type: profile.tonalType,
      duration: 0.04,
      release: 0.07,
      gain: g,
    })
    playTone(ctx, dest, {
      freq: b,
      type: profile.tonalType,
      delay: 0.05,
      duration: 0.05,
      release: 0.09,
      gain: g,
    })
  },
  'ui-toggle': (ctx, dest, profile, o) => {
    playTone(ctx, dest, {
      freq: semitone(base(profile), 9),
      type: profile.tonalType,
      duration: 0.02,
      release: 0.05,
      gain: 0.13 * (o.intensity ?? 1),
      lowpass: 4000 * profile.brightness,
    })
  },
  'ui-error': (ctx, dest, profile, o) => {
    const b = base(profile)
    const g = 0.16 * (o.intensity ?? 1)
    playTone(ctx, dest, {
      freq: semitone(b, -5),
      type: 'sawtooth',
      duration: 0.14,
      release: 0.1,
      gain: g,
      glideTo: semitone(b, -8),
      lowpass: 1400,
    })
    playTone(ctx, dest, {
      freq: semitone(b, -4),
      type: 'sawtooth',
      duration: 0.12,
      release: 0.08,
      gain: g * 0.6,
      lowpass: 1400,
    })
  },

  // -------------------------------------------------------- word / match
  'trace-step': (ctx, dest, profile, o) => {
    const step = TRACE_LADDER[(o.index ?? 0) % TRACE_LADDER.length] ?? 0
    const f = semitone(base(profile), 12 + step)
    playTone(ctx, dest, {
      freq: f,
      type: profile.tonalType,
      detune: profile.detune,
      duration: 0.04,
      attack: 0.004,
      release: 0.12,
      gain: 0.16 * (o.intensity ?? 1),
      glideTo: semitone(f, 1),
      lowpass: 5000 * profile.brightness,
    })
  },
  'word-valid': (ctx, dest, profile, o) => {
    const b = base(profile)
    const g = 0.2 * (o.intensity ?? 1)
    playChord(ctx, dest, [semitone(b, 0), semitone(b, 7), semitone(b, 12)], {
      type: profile.tonalType,
      detune: profile.detune,
      duration: 0.12,
      release: 0.3,
      gain: g,
    })
    // sparkle
    playTone(ctx, dest, {
      freq: semitone(b, 24),
      type: profile.tonalType,
      delay: 0.08,
      duration: 0.03,
      release: 0.18,
      gain: g * 0.6,
    })
  },
  'word-invalid': (ctx, dest, profile, o) => {
    const b = base(profile)
    const g = 0.18 * (o.intensity ?? 1)
    // two-note shake, dissonant
    playTone(ctx, dest, {
      freq: semitone(b, -2),
      type: 'sawtooth',
      duration: 0.07,
      release: 0.06,
      gain: g,
      lowpass: 1600,
    })
    playTone(ctx, dest, {
      freq: semitone(b, -3),
      type: 'sawtooth',
      delay: 0.09,
      duration: 0.07,
      release: 0.08,
      gain: g,
      lowpass: 1600,
    })
  },
  'hollow-reject': (ctx, dest, profile, o) => {
    const b = base(profile)
    const g = 0.2 * (o.intensity ?? 1)
    playTone(ctx, dest, {
      freq: semitone(b, -7),
      type: 'square',
      duration: 0.05,
      release: 0.06,
      gain: g * 0.8,
      lowpass: 1100,
    })
    playNoise(ctx, dest, {
      duration: 0.05,
      release: 0.05,
      gain: g * 0.5,
      filter: { type: 'lowpass', freq: 800, sweepTo: 300 },
    })
  },
  'token-clink': (ctx, dest, profile, o) => {
    const b = base(profile)
    const g = 0.18 * (o.intensity ?? 1)
    playTone(ctx, dest, {
      freq: semitone(b, 24),
      type: profile.tonalType,
      duration: 0.02,
      release: 0.12,
      gain: g,
    })
    playTone(ctx, dest, {
      freq: semitone(b, 28),
      type: profile.tonalType,
      delay: 0.03,
      duration: 0.02,
      release: 0.14,
      gain: g * 0.8,
    })
    playNoise(ctx, dest, {
      duration: 0.01,
      release: 0.04,
      gain: g * 0.3,
      filter: { type: 'highpass', freq: 4000 },
    })
  },
  'cube-spawn': (ctx, dest, profile, o) => {
    const b = base(profile)
    const f = semitone(b, 12)
    playTone(ctx, dest, {
      freq: f,
      type: profile.tonalType,
      duration: 0.04,
      attack: 0.01,
      release: 0.14,
      gain: 0.1 * (o.intensity ?? 1),
      glideTo: semitone(f, 3),
      lowpass: 4600 * profile.brightness,
    })
    playNoise(ctx, dest, {
      duration: 0.05,
      release: 0.06,
      gain: 0.04,
      filter: { type: 'bandpass', freq: profile.textureFreq, q: 0.7 },
    })
  },
  'anchor-place': (ctx, dest, profile, o) => {
    const b = base(profile)
    const g = 0.2 * (o.intensity ?? 1)
    // warm bell
    playChord(ctx, dest, [semitone(b, 7), semitone(b, 12)], {
      type: profile.organType,
      detune: profile.detune,
      duration: 0.18,
      release: 0.4,
      gain: g * 0.7,
    })
    // stone set
    playNoise(ctx, dest, {
      duration: 0.06,
      release: 0.1,
      gain: g * 0.5,
      filter: { type: 'lowpass', freq: 700 },
    })
  },
  'anchor-consume': (ctx, dest, profile, o) => {
    const b = base(profile)
    const g = 0.24 * (o.intensity ?? 1)
    playTone(ctx, dest, {
      freq: semitone(b, -12),
      type: 'sine',
      duration: 0.1,
      release: 0.25,
      gain: g,
    })
    playNoise(ctx, dest, {
      duration: 0.18,
      release: 0.2,
      gain: g * 0.7,
      filter: { type: 'lowpass', freq: 900, sweepTo: 200 },
    })
  },
  'affinity-ping': (ctx, dest, profile, o) => {
    playTone(ctx, dest, {
      freq: semitone(base(profile), 19),
      type: profile.tonalType,
      detune: profile.detune,
      duration: 0.05,
      release: 0.22,
      gain: 0.12 * (o.intensity ?? 1),
    })
  },

  // ------------------------------------------------------ empower / pieces
  empower: (ctx, dest, profile, o) => {
    // pitch 0 Knight (low) … 3 Queen (high). Each role rises a fourth.
    const roll = (o.pitch ?? 0) * 5
    const b = semitone(base(profile), roll)
    const g = 0.16 * (o.intensity ?? 1)
    playChord(ctx, dest, [b, semitone(b, 4), semitone(b, 7), semitone(b, 12)], {
      type: profile.organType,
      detune: profile.detune,
      duration: 0.4,
      attack: 0.02,
      release: 0.35,
      gain: g,
      lowpass: 2600 * profile.brightness,
    })
  },
  capture: (ctx, dest, profile, o) => {
    const b = base(profile)
    const g = 0.22 * (o.intensity ?? 1)
    playTone(ctx, dest, {
      freq: semitone(b, -10),
      type: 'sine',
      duration: 0.06,
      release: 0.14,
      gain: g,
    })
    playNoise(ctx, dest, {
      duration: 0.08,
      release: 0.12,
      gain: g * 0.5,
      filter: { type: 'lowpass', freq: 600 },
    })
  },
  'king-capture': (ctx, dest, profile, o) => {
    const b = base(profile)
    const g = 0.26 * (o.intensity ?? 1)
    // woody thud
    playTone(ctx, dest, {
      freq: semitone(b, -14),
      type: 'sine',
      duration: 0.08,
      release: 0.2,
      gain: g,
    })
    playNoise(ctx, dest, {
      duration: 0.1,
      release: 0.15,
      gain: g * 0.55,
      filter: { type: 'lowpass', freq: 500 },
    })
    // low organ stinger, descending
    playChord(ctx, dest, [semitone(b, -12), semitone(b, -5), semitone(b, 0)], {
      type: profile.organType,
      detune: profile.detune,
      delay: 0.12,
      duration: 0.5,
      release: 0.5,
      gain: g * 0.7,
      lowpass: 2000 * profile.brightness,
    })
  },

  // -------------------------------------------------------- big moments
  flop: (ctx, dest, profile, o) => {
    const b = base(profile)
    const g = 0.22 * (o.intensity ?? 1)
    // drumroll: accelerating low taps
    for (let i = 0; i < 8; i++) {
      playNoise(ctx, dest, {
        delay: i * 0.07,
        duration: 0.02,
        release: 0.04,
        gain: g * (0.3 + i * 0.05),
        filter: { type: 'lowpass', freq: 400 },
      })
    }
    // trumpet stinger (bright ascending chord)
    playChord(ctx, dest, [semitone(b, 0), semitone(b, 4), semitone(b, 7)], {
      type: profile.organType,
      detune: profile.detune,
      delay: 0.6,
      duration: 0.4,
      attack: 0.03,
      release: 0.4,
      gain: g,
      lowpass: 3200 * profile.brightness,
    })
  },
  river: (ctx, dest, profile, o) => {
    const b = base(profile)
    const g = 0.22 * (o.intensity ?? 1)
    for (let i = 0; i < 8; i++) {
      playNoise(ctx, dest, {
        delay: i * 0.07,
        duration: 0.02,
        release: 0.04,
        gain: g * (0.3 + i * 0.05),
        filter: { type: 'lowpass', freq: 360 },
      })
    }
    // deep organ chord on settle
    playChord(
      ctx,
      dest,
      [semitone(b, -12), semitone(b, -5), semitone(b, 0), semitone(b, 7)],
      {
        type: profile.organType,
        detune: profile.detune,
        delay: 0.6,
        duration: 0.6,
        attack: 0.04,
        release: 0.6,
        gain: g,
        lowpass: 2400 * profile.brightness,
      },
    )
  },
  'grow-impact': (ctx, dest, profile, o) => {
    // The court opens at the center seam — a big "doors fling open" grow-thud.
    // Templated on king-capture / anchor-consume: a deep sine sub-thud +
    // lowpass-noise body that sweeps down (the parting rumble) + a short
    // theme-tinted organ tail blooming after the impact (the new cells settle).
    const b = base(profile)
    const g = 0.26 * (o.intensity ?? 1)
    // deep sine sub-thud — the seam slams open.
    playTone(ctx, dest, {
      freq: semitone(b, -17),
      type: 'sine',
      duration: 0.09,
      release: 0.26,
      gain: g,
    })
    // lowpass-noise body sweeping down — the parting rumble.
    playNoise(ctx, dest, {
      duration: 0.22,
      attack: 0.02,
      release: 0.18,
      gain: g * 0.6,
      filter: { type: 'lowpass', freq: 600, sweepTo: 180 },
    })
    // short organ tail (theme-tinted) blooming after the impact.
    playChord(ctx, dest, [semitone(b, -5), semitone(b, 0), semitone(b, 7)], {
      type: profile.organType,
      detune: profile.detune,
      delay: 0.1,
      duration: 0.22,
      attack: 0.03,
      release: 0.3,
      gain: g * 0.6,
      lowpass: 2400 * profile.brightness,
    })
  },
  queen: (ctx, dest, profile, o) => {
    const b = base(profile)
    const g = 0.2 * (o.intensity ?? 1)
    // rich, bright major chord ~700ms — the brand signature
    playChord(
      ctx,
      dest,
      [
        semitone(b, 0),
        semitone(b, 4),
        semitone(b, 7),
        semitone(b, 12),
        semitone(b, 16),
      ],
      {
        type: profile.organType,
        detune: profile.detune,
        attack: 0.03,
        duration: 0.5,
        release: 0.25,
        gain: g,
        lowpass: 3000 * profile.brightness,
      },
    )
  },
  powerup: (ctx, dest, profile, o) => {
    const b = base(profile)
    const g = 0.18 * (o.intensity ?? 1)
    // whoosh up
    playNoise(ctx, dest, {
      duration: 0.25,
      attack: 0.08,
      release: 0.12,
      gain: g * 0.5,
      filter: { type: 'bandpass', freq: 600, sweepTo: 4000, q: 0.8 },
    })
    // bloom shimmer
    playChord(ctx, dest, [semitone(b, 12), semitone(b, 16), semitone(b, 19)], {
      type: profile.tonalType,
      detune: profile.detune,
      delay: 0.12,
      duration: 0.18,
      release: 0.3,
      gain: g * 0.8,
    })
  },
  'win-fanfare': (ctx, dest, profile, o) => {
    const b = base(profile)
    const g = 0.2 * (o.intensity ?? 1)
    const notes = [0, 4, 7, 12]
    notes.forEach((n, i) => {
      playTone(ctx, dest, {
        freq: semitone(b, n),
        type: profile.organType,
        detune: profile.detune,
        delay: i * 0.1,
        duration: 0.1,
        release: 0.3,
        gain: g,
        lowpass: 3200 * profile.brightness,
      })
    })
    playChord(ctx, dest, [semitone(b, 12), semitone(b, 16), semitone(b, 19)], {
      type: profile.organType,
      detune: profile.detune,
      delay: 0.42,
      duration: 0.4,
      release: 0.5,
      gain: g,
    })
  },
  'lose-sting': (ctx, dest, profile, o) => {
    const b = base(profile)
    const g = 0.17 * (o.intensity ?? 1)
    // somber minor descent
    playChord(ctx, dest, [semitone(b, 0), semitone(b, 3), semitone(b, 7)], {
      type: profile.organType,
      detune: profile.detune,
      duration: 0.3,
      release: 0.4,
      gain: g,
      lowpass: 1800 * profile.brightness,
    })
    playChord(ctx, dest, [semitone(b, -5), semitone(b, -2), semitone(b, 2)], {
      type: profile.organType,
      detune: profile.detune,
      delay: 0.35,
      duration: 0.45,
      release: 0.6,
      gain: g,
      lowpass: 1500 * profile.brightness,
    })
  },
  'board-flip': (ctx, dest, _profile, o) => {
    const g = 0.2 * (o.intensity ?? 1)
    // woody whump
    playNoise(ctx, dest, {
      duration: 0.12,
      release: 0.1,
      gain: g,
      filter: { type: 'lowpass', freq: 500, sweepTo: 240 },
    })
    // settle thock
    playNoise(ctx, dest, {
      delay: 0.34,
      duration: 0.03,
      release: 0.05,
      gain: g * 0.7,
      filter: { type: 'bandpass', freq: 800, q: 1.2 },
    })
  },
  'reveal-pulse': (ctx, dest, profile, o) => {
    const b = base(profile)
    playChord(ctx, dest, [semitone(b, 12), semitone(b, 19)], {
      type: profile.tonalType,
      detune: profile.detune,
      attack: 0.04,
      duration: 0.12,
      release: 0.3,
      gain: 0.12 * (o.intensity ?? 1),
    })
  },
}

/** All cue names — pinned by tests so a new cue surfaces as an intentional
 * addition (and so the engine can validate). */
export const SFX_CUES: ReadonlyArray<SfxCue> = Object.keys(CUES) as SfxCue[]
